import React, { useState, useEffect } from 'react';
import { UserX, Mail, RotateCcw } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { useUserData } from '../../hooks/useUserData';
import { posthog } from '../../providers/PostHogProvider';

/**
 * Full-page interstitial shown when subscription state is CHURNED.
 * super_admin can reactivate via Stripe customer portal.
 */
const ChurnedPage: React.FC = () => {
  const { userData } = useUserData();
  const isSuperAdmin = userData?.role === 'super_admin';
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try { posthog?.capture('subscription_state_page_viewed', { state: 'churned' }); } catch {}
  }, []);

  const handleReactivate = async () => {
    setLoading(true);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortalSession({
        companyId: userData?.companyId,
        returnUrl: window.location.origin,
      });
      const { url } = result.data as { url: string };
      window.location.href = url;
    } catch {
      toast.error('Unable to open billing portal. Please contact support.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full bg-gray-800/50 border border-gray-700 rounded-2xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-700 mb-4">
          <UserX className="text-gray-400" size={32} />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Account Cancelled</h1>
        <p className="text-gray-400 mb-6">
          Your subscription has been cancelled. Your data is preserved for 90 days.
          Reactivate your subscription to regain access.
        </p>
        <div className="space-y-3">
          {isSuperAdmin && (
            <button
              onClick={handleReactivate}
              disabled={loading}
              className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
            >
              <RotateCcw size={18} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Opening Billing Portal...' : 'Reactivate Subscription'}
            </button>
          )}
          <a
            href="mailto:support@valutten.com"
            className="flex items-center justify-center gap-2 w-full py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            <Mail size={18} />
            Contact Support
          </a>
        </div>
        {!isSuperAdmin && (
          <p className="text-sm text-gray-500 mt-6">
            Please ask your account administrator to reactivate the subscription.
          </p>
        )}
      </div>
    </div>
  );
};

export default ChurnedPage;
